import React, { Component } from "react";
import Sortly, { findDescendants, convert } from "react-sortly";
import update from "immutability-helper";

import ItemRenderer from "../item_renderer/ItemRenderer";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import i18n from "i18next";
import { isSlide, isPage } from "../../../../common/utils";
import EditorIndexTitle from "../editor_index_title/EditorIndexTitle";
import ContainedViewsList from "../contained_views_list/ContainedViewsList";
import BuildGhosts from "../../ai-chat/BuildGhosts";
import ScenesTotalDuration from "./ScenesTotalDuration";
import PropTypes from "prop-types";
import {
  getSelectableSceneOrder,
  toggleSceneSelection,
  rangeSceneSelection,
  pruneSceneSelection,
  sortByTreeOrder,
  buildGroupMoveOps,
} from "./fileTreeSelection";
import "./_fileTree.scss";

class FileTree extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showSortableItems: true,
      showContainedViews: false,
      isEditingTitle: false,
      // Scene multi-selection (Ctrl/Cmd+click, Shift+click). Ephemeral UI state,
      // deliberately kept out of Redux / undo history. Reported upward through
      // onSceneSelectionChange so the bottom Delete button can bulk-delete it.
      selectedIds: [],
      anchorId: null,
    };
    this.indexSelectedBeforeMouseDown = null;
    this.handleItemMouseDown = this.handleItemMouseDown.bind(this);
    this.handleItemClick = this.handleItemClick.bind(this);
    this.clearSelection = this.clearSelection.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDragEnd = this.handleDragEnd.bind(this);
    this.dndWindowRef = React.createRef();

    this.titleEditorRef = React.createRef();
    this.handleChange = this.handleChange.bind(this);
    this.handleMove = this.handleMove.bind(this);
    this.handleToggleCollapse = this.handleToggleCollapse.bind(this);
    this.renderItem = this.renderItem.bind(this);
    this.idealStudioNavItemsToSortlyItems = this.idealStudioNavItemsToSortlyItems.bind(this);
    this.getImmediateDescendants = this.getImmediateDescendants.bind(this);
    this.handleEditClick = this.handleEditClick.bind(this);
    this.handleTitleEditingChange = this.handleTitleEditingChange.bind(this);
  }

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    return (
      nextProps.navItems !== this.props.navItems ||
      nextProps.navItemsIds !== this.props.navItemsIds ||
      nextProps.navItemSelected !== this.props.navItemSelected ||
      nextProps.viewToolbars !== this.props.viewToolbars ||
      nextProps.carouselShow !== this.props.carouselShow ||
      nextProps.indexSelected !== this.props.indexSelected ||
      nextProps.containedViews !== this.props.containedViews ||
      nextProps.containedViewSelected !== this.props.containedViewSelected ||
      nextProps.globalConfig !== this.props.globalConfig ||
      nextProps.exercises !== this.props.exercises ||
      nextState.showSortableItems !== this.state.showSortableItems ||
      nextState.showContainedViews !== this.state.showContainedViews ||
      nextState.isEditingTitle !== this.state.isEditingTitle ||
      nextState.selectedIds !== this.state.selectedIds
    );
  }

  componentDidMount() {
    document.addEventListener("keydown", this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  componentDidUpdate(prevProps, prevState) {
    // Scenes can be deleted/replaced from anywhere (undo, import, bulk delete):
    // drop selected ids that no longer exist.
    if (prevProps.navItems !== this.props.navItems && this.state.selectedIds.length) {
      const pruned = pruneSceneSelection(this.state.selectedIds, this.props.navItems);
      if (pruned !== this.state.selectedIds) {
        this.setState({ selectedIds: pruned });
      }
    }
    if (prevState.selectedIds !== this.state.selectedIds && this.props.onSceneSelectionChange) {
      this.props.onSceneSelectionChange(
        sortByTreeOrder(this.state.selectedIds, this.props.navItemsIds)
      );
    }
  }

  handleKeyDown(e) {
    if (e.key === "Escape" && this.state.selectedIds.length) {
      this.clearSelection();
    }
  }

  clearSelection() {
    this.setState({ selectedIds: [] });
  }

  handleItemMouseDown(id) {
    // ItemRenderer calls onIndexSelected on mousedown; remember what was selected
    // before so the first Ctrl/Shift+click can include it.
    this.indexSelectedBeforeMouseDown = this.props.indexSelected;
  }

  handleItemClick(id, e) {
    const additive = e.ctrlKey || e.metaKey;
    const range = e.shiftKey;

    if (!isPage(id) || (!additive && !range)) {
      this.setState({
        selectedIds: this.state.selectedIds.length ? [] : this.state.selectedIds,
        anchorId: isPage(id) ? id : this.state.anchorId,
      });
      return;
    }

    const previous = this.indexSelectedBeforeMouseDown;
    const anchorId = this.state.anchorId || (isPage(previous) ? previous : null);

    if (range && anchorId) {
      if (window.getSelection) window.getSelection().removeAllRanges();
      const order = getSelectableSceneOrder(this.props.navItemsIds, this.props.navItems);
      this.setState({ selectedIds: rangeSceneSelection(order, anchorId, id), anchorId });
      return;
    }

    const base =
      this.state.selectedIds.length || !anchorId || anchorId === id
        ? this.state.selectedIds
        : [anchorId];
    const selectedIds = toggleSceneSelection(base, id);
    this.setState({ selectedIds, anchorId: id });

    // Mousedown already made the clicked scene active. When the click removed it
    // from the selection, hand the active highlight back to a scene that is still
    // selected — otherwise the deselected row keeps looking selected.
    if (!selectedIds.includes(id) && selectedIds.length) {
      const nextActive = selectedIds.includes(previous)
        ? previous
        : selectedIds[selectedIds.length - 1];
      this.props.onIndexSelected(nextActive);
    }
  }

  // Group drag feedback is applied straight to the DOM: a FileTree re-render
  // mid-drag would hand Sortly a fresh items array and reset the in-progress
  // order (Sortly copies props.items into its state on every change).
  handleDragStart(dragIndex) {
    const id = this.props.navItemsIds[dragIndex];
    const { selectedIds } = this.state;
    if (this.dndWindowRef.current && selectedIds.length > 1 && selectedIds.includes(id)) {
      this.dndWindowRef.current.classList.add("groupDragging");
    }
  }

  handleDragEnd() {
    if (this.dndWindowRef.current) {
      this.dndWindowRef.current.classList.remove("groupDragging");
    }
  }

  handleMove(items, index, newIndex) {
    const { path } = items[newIndex];
    const parent = items.find((item) => item.id === path[path.length - 1]);

    // parent should not be file
    if (parent && parent.type === "file") {
      return false;
    }

    // if parent is collapsed, should expand the parent
    if (parent && parent.collapsed) {
      const updateFn = {
        [items.indexOf(parent)]: { collapsed: { $set: false } },
      };
      const descendants = findDescendants(items, items.indexOf(parent));
      descendants.forEach((item) => {
        updateFn[items.indexOf(item)] = { collapsed: { $set: false } };
      });

      return update(items, updateFn);
    }
    return true;
  }

  idealStudioNavItemsToSortlyItems(edNavItems, edNavItemsId, edViewToolbars) {
    let edItems = edNavItemsId.map((item, i) => {
      return {
        id: edNavItems[item].id,
        name: edViewToolbars[item].viewName,
        type: edNavItems[item].type === "section" ? "folder" : "file",
        edType: edNavItems[item].type,
        index: edNavItems[edNavItems[item].parent].children.indexOf(item),
        parentId: edNavItems[item].parent,
        collapsed: !edNavItems[item].isExpanded,
      };
    });
    // Sortly convert metodu sahnelerin olması gereken sırasını değiştiriyordu.
    // Bu nedenle convert metodundan sonra edItems'taki sıralamayı tekrar geri alıyoruz
    let edItemsConverted = convert(edItems);
    // Selection flags ride on the Sortly items themselves so each Item's props
    // change (and it re-renders) when the multi-selection changes.
    const { selectedIds } = this.state;
    let edItems2 = edNavItemsId.map((item, i) => {
      const converted = edItemsConverted.find((a) => a.id === item);
      return {
        ...converted,
        multiSelected: selectedIds.includes(item),
        multiSelectCount: selectedIds.length,
      };
    });
    return edItems2;

    //return convert(edItems);
  }

  handleChange(items) {
    let movedItem = items.find((i) => i.id === this.props.indexSelected);

    const groupOps =
      movedItem &&
      buildGroupMoveOps(
        items,
        movedItem.id,
        this.state.selectedIds,
        this.props.navItems,
        this.props.navItemsIds
      );
    if (groupOps) {
      this.handleGroupMove(groupOps);
      return;
    }

    let oldParentId = this.props.navItems[movedItem.id].parent;
    let newParentId = movedItem.path[movedItem.path.length - 1] || 0;
    let idsInOrder = items.map((item) => item.id);
    let childrenInOrder = this.getImmediateDescendants(items, newParentId);

    if (newParentId !== 0) {
      let shouldChildExpand =
        movedItem.type === "file" && this.props.navItems[newParentId].isExpanded;
      this.props.onNavItemExpanded(movedItem.id, shouldChildExpand);
    }
    this.props.onNavItemReordered(
      movedItem.id,
      newParentId,
      oldParentId,
      idsInOrder,
      childrenInOrder
    );
  }

  // Multi-selection drag: one REORDER_NAV_ITEM per selected scene, grouped into a
  // single undo step (same window.cropUndoGroup hook the crop flow uses).
  handleGroupMove(ops) {
    const { navItems } = this.props;
    const previousGroup = window.cropUndoGroup;
    window.cropUndoGroup =
      "scene-bulk-move_" + Date.now() + "_" + Math.random().toString(36).slice(2);
    try {
      ops.forEach((op) => {
        if (op.newParent !== 0) {
          this.props.onNavItemExpanded(op.id, !!navItems[op.newParent].isExpanded);
        }
        this.props.onNavItemReordered(
          op.id,
          op.newParent,
          op.oldParent,
          op.idsInOrder,
          op.childrenInOrder
        );
      });
    } finally {
      window.cropUndoGroup = previousGroup;
    }
  }

  getImmediateDescendants(items, parentId) {
    return parentId === 0
      ? items.filter((i) => i.path.length === 0).map((i) => i.id)
      : findDescendants(
          items,
          items.findIndex((i) => i.id === parentId)
        )
          .filter((i) => i.path.slice(-1)[0] === parentId)
          .map((i) => i.id);
  }

  handleEditClick(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Start editing mode
    if (this.titleEditorRef.current) {
      this.titleEditorRef.current.startEditing();
      this.setState({ isEditingTitle: true });
    }
  }

  handleTitleEditingChange(isEditing) {
    this.setState({ isEditingTitle: isEditing });
  }

  handleToggleCollapse(index) {
    let items = this.idealStudioNavItemsToSortlyItems(
      this.props.navItems,
      this.props.navItemsIds,
      this.props.viewToolbars
    );
    const descendants = findDescendants(items, index);
    const parentId = items[index].id;
    const expandedItemId = items[index].id;
    const expands = items[index].collapsed;

    this.props.onNavItemExpanded(expandedItemId, expands);
  }

  renderItem(props) {
    return (
      <ItemRenderer
        {...props}
        onToggleCollapse={this.handleToggleCollapse}
        onIndexSelected={this.props.onIndexSelected}
        onNavItemSelected={this.props.onNavItemSelected}
        onNavItemNameChanged={this.props.onNavItemNameChanged}
        onNavItemToggled={this.props.onNavItemToggled}
        navItems={this.props.navItems}
        viewToolbars={this.props.viewToolbars}
        containedViewSelected={this.props.containedViewSelected}
        navItemSelected={this.props.navItemSelected}
        indexSelected={this.props.indexSelected}
        onItemMouseDown={this.handleItemMouseDown}
        onItemClick={this.handleItemClick}
      />
    );
  }

  getContentHeight() {
    if (!this.state.showSortableItems && !this.state.showContainedViews) {
      return "0px";
    } else if (this.props.globalConfig?.hideGlobalScore) {
      return "calc(100vh - 262px)";
    } else if (this.state.showSortableItems && !this.state.showContainedViews) {
      return "calc(100vh - 320px)";
    } else if (!this.state.showSortableItems && this.state.showContainedViews) {
      return "0px";
    } else {
      return "calc((100vh - 320px) / 2)";
    }
  }

  getContainedViewsHeight() {
    if (!this.state.showContainedViews) {
      return "0px";
    } else if (this.props.globalConfig?.hideGlobalScore) {
      return "calc(100vh - 262px)";
    } else if (!this.state.showSortableItems && this.state.showContainedViews) {
      return "calc(100vh - 320px)";
    } else if (this.state.showSortableItems && this.state.showContainedViews) {
      return "calc((100vh - 320px) / 2)";
    }
    return "0px";
  }

  calculateTotalScore() {
    let total = 0;
    if (!this.props.exercises) return 0;
    Object.keys(this.props.exercises).forEach((pageId) => {
      if (pageId.indexOf("cv-") !== 0) {
        let pageExercises = this.props.exercises[pageId].exercises || {};
        Object.values(pageExercises).forEach((ex) => {
          if (ex.weight) {
            total += parseFloat(ex.weight);
          }
        });
      }
    });
    return total;
  }

  render() {
    const courseTitle = this.props.courseTitle || i18n.t("course_title");

    // Sidebar kapalıyken sadece menu butonunu göster
    if (!this.props.carouselShow) {
      return (
        <div className="fileTreeCollapsed">
          {this.props.onToggleWidth && (
            <button className="menuButton collapsed" onClick={() => this.props.onToggleWidth()}>
              <i className="material-icons menuIcon">menu</i>
            </button>
          )}
        </div>
      );
    }
    const totalScore = this.calculateTotalScore();
    return (
      <div className="carousselListContainer">
        {/* Doc name section */}
        <div className="docNameSection">
          <div className="docNameContent">
            {this.props.onToggleWidth && (
              <button
                className="menuButton expanded panelCloseBtn"
                onClick={() => this.props.onToggleWidth()}
              >
                <i className="material-icons panelCloseBtnIcon">close</i>
              </button>
            )}
            <EditorIndexTitle
              ref={this.titleEditorRef}
              id="coursetit"
              className="tituloCurso"
              title={courseTitle}
              courseTitle
              onNameChanged={this.props.onTitleChanged}
              onEditingChange={this.handleTitleEditingChange}
            />
          </div>
        </div>

        {/* Total Score Display */}
        {totalScore > 0 && (
          <div className="totalScoreDisplay">
            {i18n.t("global_config.total_score")}:
            <span className="totalScoreValue">{totalScore}</span>
          </div>
        )}

        {/* Index Section */}
        <div
          id="sortablesCollapse"
          className="sectionHeader"
          onClick={() => {
            this.setState({ showSortableItems: !this.state.showSortableItems });
          }}
        >
          <span className="sectionTitle">{i18n.t("INDEX")}</span>
          <span className="sectionHeaderRight">
            <ScenesTotalDuration />
            <i className="material-icons sectionIcon">
              {this.state.showSortableItems ? "keyboard_arrow_down" : "keyboard_arrow_right"}
            </i>
          </span>
        </div>
        <div
          ref={this.dndWindowRef}
          className="DnD-Window carList dndWindow"
          style={{
            maxHeight: this.getContentHeight(),
            height: this.state.showSortableItems ? this.getContentHeight() : "0px",
            padding: this.state.showSortableItems ? "0 16px 16px 16px" : "0",
          }}
        >
          <section className="dndSection">
            <div className="row dndRow">
              <div className="col-12 col-lg-8 col-xl-6 dndCol">
                <Sortly
                  items={this.idealStudioNavItemsToSortlyItems(
                    this.props.navItems,
                    this.props.navItemsIds,
                    this.props.viewToolbars
                  )}
                  itemRenderer={this.renderItem}
                  onMove={this.handleMove}
                  onDragStart={this.handleDragStart}
                  onDragEnd={this.handleDragEnd}
                  onChange={this.handleChange}
                />
                <BuildGhosts realIds={this.props.navItemsIds} />
              </div>
            </div>
          </section>
        </div>

        {/* Contained Views Section */}
        <div
          id="scontainedViewsCollapse"
          className="sectionHeader withBorder"
          onClick={() => {
            this.setState({ showContainedViews: !this.state.showContainedViews });
          }}
        >
          <span className="sectionTitle">{i18n.t("CONTAINED_VIEWS")}</span>
          <i className="material-icons sectionIcon">
            {this.state.showContainedViews ? "keyboard_arrow_down" : "keyboard_arrow_right"}
          </i>
        </div>
        <ContainedViewsList
          showContainedViews={this.state.showContainedViews}
          showSortableItems={this.state.showSortableItems}
          contentHeight={this.getContainedViewsHeight()}
          carouselShow={this.props.carouselShow}
          containedViews={this.props.containedViews}
          containedViewSelected={this.props.containedViewSelected}
          boxes={this.props.boxes}
          navItemsIds={this.props.navItemsIds}
          navItems={this.props.navItems}
          navItemSelected={this.props.navItemSelected}
          indexSelected={this.props.indexSelected}
          onBoxAdded={this.props.onBoxAdded}
          onContainedViewDeleted={this.props.onContainedViewDeleted}
          onContainedViewSelected={this.props.onContainedViewSelected}
          onContainedViewNameChanged={this.props.onContainedViewNameChanged}
          onNavItemNameChanged={this.props.onNavItemNameChanged}
          onNavItemAdded={this.props.onNavItemAdded}
          onNavItemSelected={this.props.onNavItemSelected}
          onIndexSelected={this.props.onIndexSelected}
          onNavItemExpanded={this.props.onNavItemExpanded}
          onNavItemDeleted={this.props.onNavItemDeleted}
          onNavItemReordered={this.props.onNavItemReordered}
          viewToolbars={this.props.viewToolbars}
        />
      </div>
    );
  }
}

const overrideDropCaptureHandler = (manager) => {
  const backend = HTML5Backend(manager);
  const orgTopDropCapture = backend.handleTopDropCapture;

  backend.handleTopDropCapture = (e) => {
    let classes = e.target.className.split(" ");
    if (e.target.tagName === "INPUT" && e.target.type === "file") {
      e.stopPropagation();
    } else if (classes.includes("file") || classes.includes("folder")) {
      orgTopDropCapture.call(backend, e);
    }
  };

  return backend;
};

FileTree.propTypes = {
  /**
   * Global parent of navItems (0)
   */
  id: PropTypes.number,
  /**
   * Indicates whether the carousel has been expanded or not
   */
  carouselShow: PropTypes.bool,
  /**
   *  Object containing all contained views (identified by its ID)
   */
  containedViews: PropTypes.object,
  /**
   * Selected contained view
   */
  containedViewSelected: PropTypes.any,
  /**
   * View/Contained view selected at the index
   */
  indexSelected: PropTypes.any,
  /**
   * Dictionary containing all created views, each one with its *id* as the key
   */
  navItems: PropTypes.object,
  /**
   * Current selected view (by ID)
   */
  navItemSelected: PropTypes.any,
  /**
   *  View/Contained view selected at the index
   */
  navItemsIds: PropTypes.array,
  /**
   * Callback for adding a new box
   */
  onBoxAdded: PropTypes.func,
  /**
   * Callback for selecting contained view
   */
  onContainedViewNameChanged: PropTypes.func,
  /**
   * Callback for renaming contained view
   */
  onContainedViewSelected: PropTypes.func,
  /**
   * Callback for deleting contained view
   */
  onContainedViewDeleted: PropTypes.func,
  /**
   * Callback for renaming view
   */
  onIndexSelected: PropTypes.func,
  /**
   * Adds a new view
   */
  onNavItemAdded: PropTypes.func,
  /**
   * Expands navItem (only for sections)
   */
  onNavItemExpanded: PropTypes.func,
  /**
   * Callback for renaming view
   */
  onNavItemNameChanged: PropTypes.func,
  /**
   * Callback for reordering navItems
   */
  onNavItemReordered: PropTypes.func,
  /**
   * Deletes a view
   */
  onNavItemDeleted: PropTypes.func,
  /**
   * Reports the scene multi-selection (ids in tree order) whenever it changes
   */
  onSceneSelectionChange: PropTypes.func,
  /**
   * Selects a view
   */
  onNavItemSelected: PropTypes.func,
  /**
   * Object containing all the pages' toolbars
   */
  viewToolbars: PropTypes.object,
  /**
   * Object containing all the pages' toolbars
   */
  name: PropTypes.string,
  /**
   * Object containing all the pages' toolbars
   */
  type: PropTypes.oneOf(["folder", "file"]),
  /**
   * Indicates if objects is collapsed (not expanded)
   */
  collapsed: PropTypes.bool,
  /**
   * Function to connect Drag source
   */
  connectDragSource: PropTypes.func,
  /**
   * Manages preview when dragging
   */
  connectDragPreview: PropTypes.func,
  /**
   * Function to connect Drop target
   */
  connectDropTarget: PropTypes.func,
  /**
   * Boolean that indicates if object is dragging
   */
  isDragging: PropTypes.bool,
  /**
   *  Object containing all created boxes (by id)
   */
  boxes: PropTypes.object,
  /**
   * Course title
   */
  courseTitle: PropTypes.string,
  /**
   * Callback for changing course title
   */
  onTitleChanged: PropTypes.func,
  /**
   * Callback for toggling carousel width
   */
  onToggleWidth: PropTypes.func,
  /**
   * Toggle navItem visibility (hide/show in visor)
   */
  onNavItemToggled: PropTypes.func,
};

// HOC pattern replaced with DndProvider in parent component
export default FileTree;
